import type { ReactElement } from 'react';
import {
  type PaymentProviderFilter,
  type PaymentStatusFilter,
  usePaymentsListQuery,
} from '@/entities/payment';
import { cn, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { PaymentRow } from './payment-row';

interface PaymentsTableProps {
  readonly page: number;
  readonly statusFilter: PaymentStatusFilter;
  readonly providerFilter?: PaymentProviderFilter;
  readonly onPageChange: (page: number) => void;
  readonly onOpen: (paymentId: string) => void;
}

const COLUMNS = [
  { label: '승인 일시' },
  { label: '주문 ID' },
  { label: '회원' },
  { label: '금액', className: 'text-right' },
  { label: '남은 금액', className: 'text-right' },
  { label: '상태' },
  { label: '결제수단' },
] as const;

/**
 * 결제 목록 표 — 조회·페이지 이동을 묶은 자립 블록(결제 설계 §12). 주문 목록과 같은 흰 표 카드이고,
 * 읽는 중·실패·빈 결과는 카드 안 가운데 안내(EmptyState)로 보인다.
 */
export const PaymentsTable = ({
  page,
  statusFilter,
  providerFilter,
  onPageChange,
  onOpen,
}: PaymentsTableProps): ReactElement => {
  const paymentsQuery = usePaymentsListQuery({ page, statusFilter, providerFilter });

  if (paymentsQuery.isPending) {
    return (
      <div className="mt-5">
        <EmptyState>불러오는 중…</EmptyState>
      </div>
    );
  }

  if (paymentsQuery.isError) {
    return (
      <div className="mt-5">
        <EmptyState isError>목록을 불러오지 못했습니다: {paymentsQuery.error.message}</EmptyState>
      </div>
    );
  }

  if (paymentsQuery.data.items.length === 0) {
    return (
      <div className="mt-5">
        <EmptyState>조건에 맞는 결제가 없습니다.</EmptyState>
      </div>
    );
  }

  return (
    <>
      <div className={cn('mt-5', tableStyles.card)}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.label}
                  className={cn(tableStyles.th, 'className' in column && column.className)}
                >
                  {column.label}
                </th>
              ))}
              <th className={tableStyles.th}>
                <span className="sr-only">상세</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paymentsQuery.data.items.map((payment) => (
              <PaymentRow key={payment.id} payment={payment} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        className="mt-5 justify-center"
        page={paymentsQuery.data.meta.page}
        totalPages={paymentsQuery.data.meta.totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
};
